import { ChevronDown } from '@/components/ui/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useHomepage } from '@/contexts/homepage-context';
import {
  IMPLICIT_PRIMITIVE_LABELS,
  LATTICE_FILL_DEFAULTS,
  NASA_LATTICE_TOPOLOGY_ORDER,
  NasaLatticeTopology,
} from '@/config/implicitPrimitives';

import {
  pickerContentClass,
  pickerDisablableItemBaseClass,
  pickerItemActiveClass,
  pickerItemInactiveClass,
  pillWidthClass,
} from '../dropdownStyles';

import { SubTab } from './data';

interface NasaUnitcellConverterDropdownProps {
  tab: SubTab;
}

// Toolbar pill for the NASA Unitcell Lattice Converter (epic #309). Unlike the
// NASA Lattice Primitives dropdown — which mints a single unit cell and maps
// each topology to its own ImplicitPrimitive — the converter is ONE primitive
// ('lattice-fill') whose lattice STYLE is a parameter. Picking a style here just
// updates params.topology and locks the converter sub-tab; the upload + tuning
// happen in the feature-tree panel (LatticeConverterContent). The full
// NASA_LATTICE_DISABLED set does NOT apply: the converter fills on the PicoGK
// kernel (not the single-cell Python path that 504s on the disabled TPMS).
export function NasaUnitcellConverterDropdown({
  tab,
}: NasaUnitcellConverterDropdownProps) {
  const {
    vehicleSubTab,
    implicitPrimitiveParams,
    setVehicleSubTab,
    setImplicitPrimitive,
    setImplicitPrimitiveParams,
  } = useHomepage();

  const isActive = vehicleSubTab === tab.value;
  const activeTopology =
    implicitPrimitiveParams.kind === 'lattice-fill'
      ? implicitPrimitiveParams.params.topology
      : null;
  const activeStyleLabel =
    isActive && activeTopology
      ? IMPLICIT_PRIMITIVE_LABELS[activeTopology]
      : 'Select Lattice Style';

  const handlePick = (t: NasaLatticeTopology) => {
    setVehicleSubTab('nasa-unitcell-converter');
    setImplicitPrimitive('lattice-fill');
    // Preserve an already-uploaded part + tuned values when switching style;
    // only the topology changes. Falls back to defaults on first pick.
    const prev =
      implicitPrimitiveParams.kind === 'lattice-fill'
        ? implicitPrimitiveParams.params
        : LATTICE_FILL_DEFAULTS;
    setImplicitPrimitiveParams({
      kind: 'lattice-fill',
      params: { ...prev, topology: t },
    });
  };

  return (
    <DropdownMenu key={tab.value}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            pillWidthClass,
            'flex items-center justify-center gap-2 self-stretch overflow-hidden rounded-md border px-4 text-center text-sm transition-colors',
            isActive
              ? 'border-[#107d40] bg-[#070909] text-[#107d40] hover:border-[#107d40] hover:bg-[#0f3d24]'
              : 'border-[#737373] text-telekinesis-neutral-50 hover:border-[#107d40] hover:bg-[#0f3d24]',
          )}
        >
          <div className="flex min-w-0 flex-1 flex-col items-center">
            {/* Shortened from tab.label ("NASA Unitcell Lattice Converter")
                — the full name doesn't fit two lines at the standardized pill
                width; the dropdown content still uses the full name. */}
            <span className="w-full truncate text-center leading-tight">
              Lattice Converter
            </span>
            <span className="mt-0.5 flex w-full items-center justify-center gap-1 text-xs leading-tight opacity-80">
              <span className="min-w-0 truncate">{activeStyleLabel}</span>
              <ChevronDown className="h-3 w-3 flex-shrink-0" />
            </span>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={pickerContentClass}
        style={{
          minWidth: 'var(--radix-dropdown-menu-trigger-width)',
        }}
      >
        {NASA_LATTICE_TOPOLOGY_ORDER.map((t) => (
          <DropdownMenuItem
            key={t}
            onClick={() => handlePick(t)}
            className={cn(
              pickerDisablableItemBaseClass,
              'flex items-center justify-between py-2 hover:cursor-pointer hover:bg-[#0f3d24]',
              activeTopology === t
                ? pickerItemActiveClass
                : pickerItemInactiveClass,
            )}
          >
            <span className="text-sm font-medium">
              {IMPLICIT_PRIMITIVE_LABELS[t]}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
